import { logger } from '../utils/logger'
import { config, validateConfig } from './index'

const SENSITIVE_KEY = /secret|token|key|password/i
const REDACTED = '[REDACTED]'
const URL_USERINFO = /^([a-z][a-z0-9+.-]*:\/\/)[^/?#@\s]+@/i
const URL_QUERY_PARAM = /([?&;])([^=&#;]+)=([^&#;]*)/g

function redactString(value: string): string {
  return value
    .replace(URL_USERINFO, `$1${REDACTED}@`)
    .replace(URL_QUERY_PARAM, (match, sep: string, name: string) =>
      SENSITIVE_KEY.test(name) ? `${sep}${name}=${REDACTED}` : match,
    )
}

/**
 * Deep-copies `value`, masking fields whose key matches /secret|token|key|password/i and
 * credentials embedded in URL strings (userinfo, sensitive query params). Startup config is
 * shipped to the backend log endpoint whenever debug is enabled, including in production.
 */
export function redactSensitive<T>(value: T): T {
  if (typeof value === 'string') return redactString(value) as T
  if (Array.isArray(value)) return value.map((item) => redactSensitive(item)) as T
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = SENSITIVE_KEY.test(k) ? REDACTED : redactSensitive(v)
    }
    return out as T
  }
  return value
}

/** Logs the resolved configuration (dev/debug) and production config errors at app start. */
export function logStartupConfig(): void {
  if (config.env.isDevelopment || config.features.debug) {
    logger.debug('TAGP configuration', redactSensitive({
      mode: config.env.mode,
      api: {
        baseUrl: config.api.baseUrl,
        agentserveUrl: config.api.agentserveUrl,
        aiworkflowsUrl: config.api.aiworkflowsUrl,
        aiservicesUrl: config.api.aiservicesUrl,
        enterpriseOntologyUrl: config.api.enterpriseOntologyUrl,
        notificationsUrl: config.api.notificationsUrl,
        monitoringUrl: config.api.monitoringUrl,
        voiceGatewayUrl: config.api.voiceGatewayUrl,
      },
      app: {
        name: config.app.name,
        version: config.app.version,
      },
      features: config.features,
    }))
  }

  if (config.env.isProduction) {
    const errors = validateConfig()
    if (errors.length > 0) {
      logger.error('Configuration errors', { errors })
    }
  }
}
